"use client";

import { cn } from "@/lib/utils";
import {
  animate,
  motion,
  MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from "framer-motion";
import { usePathname } from "next/navigation";
import React, { useEffect } from "react";

type MenuState = {
  isMobile: boolean;
  state: "idle" | "open" | "close";
  toggle: () => void;
  close: () => void;
  open: () => void;
};

const initialMenuState: MenuState = {
  isMobile: false,
  state: "idle",
  toggle: () => {},
  close: () => {},
  open: () => {},
};

const useMenuState = (breakpoint: number): MenuState => {
  const [state, setState] = React.useState<"idle" | "open" | "close">(() => {
    return "idle";
  });

  const pathname = usePathname();

  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => {
      const isBelowBreakpoint = window.innerWidth < breakpoint;
      setState((prev) => {
        if (isBelowBreakpoint) {
          return prev === "idle" ? "close" : prev;
        }
        return prev !== "idle" ? "open" : prev;
      });
      setIsMobile(isBelowBreakpoint);
    };
    mql.addEventListener("change", onChange);
    onChange();

    return () => mql.removeEventListener("change", onChange);
  }, [breakpoint]);

  useEffect(() => {
    return () => {
      setState("idle");
    };
  }, [pathname]);

  return {
    isMobile,
    state,
    toggle: () =>
      setState((prev) => {
        switch (prev) {
          case "idle":
            const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
            return mql.matches ? "open" : "close";
          case "open":
            return "close";
          case "close":
            return "open";
        }
      }),
    close: () => setState("close"),
    open: () => setState("open"),
  };
};

const ConversationLayoutContext = React.createContext<{
  conversationsMenu: MenuState;
  conversationParticipantMenu: MenuState;
  swipeProgressLeft: MotionValue<number>;
  swipeProgressRight: MotionValue<number>;
} | null>(null);

function ConversationsLayoutProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const conversationsMenu = useMenuState(1024);
  const conversationParticipantMenu = useMenuState(1440);
  const swipeProgressLeft = useMotionValue(0);
  const swipeProgressRight = useMotionValue(0);

  return (
    <ConversationLayoutContext.Provider
      value={{
        conversationsMenu,
        conversationParticipantMenu,
        swipeProgressLeft,
        swipeProgressRight,
      }}
    >
      {children}
    </ConversationLayoutContext.Provider>
  );
}

export default ConversationsLayoutProvider;

export const useConversationsMenu = () => {
  const context = React.useContext(ConversationLayoutContext);
  if (!context) {
    throw new Error(
      "useConversationsMenu must be used within ConversationsLayoutProvider",
    );
  }
  return context.conversationsMenu;
};

export const useConversationParticipantMenu = () => {
  const context = React.useContext(ConversationLayoutContext);
  if (!context) {
    throw new Error(
      "useConversationParticipantMenu must be used within ConversationsLayoutProvider",
    );
  }
  return context.conversationParticipantMenu;
};

export const useSwipeProgress = () => {
  const context = React.useContext(ConversationLayoutContext);
  if (!context) {
    throw new Error(
      "useSwipeProgress must be used within ConversationsLayoutProvider",
    );
  }
  return {
    swipeProgressLeft: context.swipeProgressLeft,
    swipeProgressRight: context.swipeProgressRight,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withConversationsLayout<T extends React.ComponentType<any>>(
  Component: React.ComponentType<React.ComponentProps<T>>,
): React.ComponentType<React.ComponentProps<T>> {
  return function WithConversationsLayout(props: React.ComponentProps<T>) {
    return (
      <ConversationsLayoutProvider>
        <Component {...props} />
      </ConversationsLayoutProvider>
    );
  };
}

type SidebarMenuProps = {
  className?: string;
  state: "idle" | "open" | "close";
  breakpoint: "lg" | "1.5xl";
  children: React.ReactNode;
  position: "left" | "right";
  action?: () => void;
  /** CSS variable for the menu width. Default: "[--menu-width:calc(--spacing(100))]" */
  width?: `[--menu-width:${string}]`;
};

export function SidebarMenu({
  state,
  breakpoint,
  children,
  position = "left",
  action,
  className,
  width = "[--menu-width:calc(--spacing(100))]",
}: SidebarMenuProps) {
  const { swipeProgressLeft, swipeProgressRight } = useSwipeProgress();
  const swipeProgress =
    position === "left" ? swipeProgressLeft : swipeProgressRight;

  const displayProgress = useMotionValue(state === "open" ? 1 : 0);

  useMotionValueEvent(swipeProgress, "change", (v) => {
    if (v > 0) {
      displayProgress.set(v);
    } else if (state === "close") {
      animate(displayProgress, 0, { duration: 0.3 });
    }
  });

  React.useEffect(() => {
    if (state === "open") {
      animate(displayProgress, 1, { duration: 0.3 });
    }
    if (state === "close") {
      animate(displayProgress, 0, { duration: 0.3 });
    }
  }, [state, displayProgress]);

  const xTransform = useTransform(
    displayProgress,
    (p) => `${position === "left" ? (1 - p) * -100 : (1 - p) * 100}%`,
  );
  const maxWidthTransform = useTransform(
    displayProgress,
    (p) => `min(calc(${p} * var(--menu-width)), 100vw)`,
  );

  const classNames = {
    lg: {
      base: "lg:sticky lg:bottom-auto lg:w-(--menu-width)",
      idle: "w-0 lg:w-(--menu-width)",
      open: "lg:z-0",
    },
    "1.5xl": {
      base: "1.5xl:sticky 1.5xl:bottom-auto 1.5xl:w-(--menu-width)",
      idle: "w-0 1.5xl:w-(--menu-width)",
      open: "1.5xl:z-0",
    },
  };

  if (state === "idle") {
    return (
      <>
        <div
          className={cn(
            "absolute inset-y-0 right-0 left-0 z-10 max-w-(--menu-width) shrink-0 overflow-hidden transition-transform duration-300",
            width,
            classNames[breakpoint].base,
            classNames[breakpoint].idle,
            {
              "left-auto": position === "right",
              "right-auto": position === "left",
            },
          )}
        >
          <div
            className={cn(
              "absolute inset-0 flex h-full w-(--menu-width) max-w-full shrink-0 flex-col bg-[rgb(241_241_241)] dark:bg-[rgb(19_19_21)]",
              className,
            )}
          >
            {children}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <motion.div
        className={cn(
          "absolute inset-y-0 z-10 shrink-0 overflow-hidden",
          width,
          position === "right" ? "right-0 left-auto" : "right-auto left-0",
          classNames[breakpoint].base,
        )}
        style={{
          width: maxWidthTransform,
          maxWidth: maxWidthTransform,
          minWidth:
            state === "open" ? "min(var(--menu-width), 100vw)" : undefined,
        }}
      >
        <motion.div
          className="absolute inset-y-0 right-0 left-0 flex h-full w-full min-w-full shrink-0"
          style={{ x: xTransform }}
        >
          {/* Content wrapper: fills slide panel, never shrinks; fixed menu width on lg+ */}
          <div
            className={cn(
              "absolute flex h-full w-full min-w-[min(var(--menu-width),100vw)] shrink-0 flex-col bg-[rgb(241_241_241)] lg:w-(--menu-width) lg:min-w-(--menu-width) dark:bg-[rgb(19_19_21)]",
              {
                "right-0": position === "left",
                "left-0": position === "right",
              },
              className,
            )}
          >
            {children}
          </div>
        </motion.div>
      </motion.div>

      {state === "open" && (
        <div
          className="1.5xl:hidden fixed inset-0 z-5 bg-zinc-950/50 transition duration-500 dark:bg-zinc-950/75"
          onClick={action}
        />
      )}
    </>
  );
}
